"use client";
import {
  ReserveFieldRecurringAdvancedForm,
  AdvancedFormState
} from "@/features/reserve/components/ReserveFieldRecurringAdvancedForm";
import { FormField } from "@/ui/form";
import { isRBFeature } from "@gettactic/helpers/src/featureFlags";
import { SaveReservationData } from "@/features/reserve/utils/utils-reserve";
import { useAuthenticated } from "@gettactic/hooks";
import { InformationCircleIcon } from "@heroicons/react/solid";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@gettactic/components/src/primitives/Popover/Popover";
import { addDays, format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import {
  UnifiedSelect,
  SelectItem as UnifiedSelectItem
} from "@cmp-src/form/UnifiedSelect";
import { RRule, Weekday } from "rrule";
import {
  byWeekDay,
  parseRRuleString,
  getCleanRRuleString,
  createCleanRRuleFromFixedValue
} from "./reserveRecurringUtils";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import { RecurringIcon } from "@cmp-src/icons/TacticIcons/Recurring";
import { inputV2Classes } from "@/features/reserve/components/utils/classes";
import { canScheduleOthers } from "@/lib/permissions";
import { getReservationExtensionDays } from "@gettactic/helpers/src/resources/defines";
import { Office, ResourceType } from "@gettactic/api";
import { useReserveForm } from "@cmp-src/reserve/ReverseFormContext";
import { logError } from "@gettactic/helpers";

type Props = {
  defaultValue: SaveReservationData["recurring"];
  form: UseFormReturn<any>;
  errors: FieldErrors<SaveReservationData>;
  isCompact?: boolean;
  office?: Office | null;
  resourceType?: ResourceType;
  originalSchedule?: any; // Pass the original schedule to check for "never ending" recurring tasks
};

const recurringOptionsTemplate: {
  label: string;
  value: RecurringFixedValues;
  debugRRule?: string;
}[] = [
  { label: "Does not repeat", value: "no" },
  { label: "", value: "daily" },
  { label: "", value: "weekly" },
  { label: "", value: "monthly" },
  { label: "", value: "annually" },
  { label: "", value: "every-weekday" },
  { label: "", value: "custom" }
];

const recurringOptionsTemplateOnlyCustom: {
  label: string;
  value: RecurringFixedValues;
  debugRRule?: string;
}[] = [
  { label: "Does not repeat", value: "no" },
  { label: "Recurring", value: "custom" }
];

function formatLabel(
  label: string,
  date: Date,
  state: AdvancedFormState | null,
  rule: RRule
) {
  return rule.toText();
}

type RecurringFixedValues =
  | "no"
  | "daily"
  | "weekly"
  | "monthly"
  | "annually"
  | "every-weekday"
  | "custom";

function getFixedDefaultValue(
  fixed: RecurringFixedValues,
  date: Date
): {
  rule: RRule;
  label?: string;
} {
  let rule = new RRule();
  if (fixed === "daily") {
    rule = new RRule({
      freq: RRule.DAILY,
      interval: 1,
      byweekday: [],
      dtstart: date,
      until: null
    });
    return { rule };
    // biome-ignore lint/style/noUselessElse: <it's intentional>
  } else if (fixed === "weekly") {
    rule = new RRule({
      freq: RRule.WEEKLY,
      interval: 1,
      byweekday: [],
      dtstart: date,
      until: null
    });
    return { rule, label: `Weekly on ${format(date, "iiii")}` };
    // biome-ignore lint/style/noUselessElse: <it's intentional>
  } else if (fixed === "monthly") {
    rule = new RRule({
      freq: RRule.MONTHLY,
      interval: 1,
      byweekday: byWeekDay(date),
      dtstart: date,
      until: null
    });
    return { rule };
    // biome-ignore lint/style/noUselessElse: <it's intentional>
  } else if (fixed === "annually") {
    rule = new RRule({
      freq: RRule.YEARLY,
      interval: 1,
      byweekday: [],
      dtstart: date,
      until: null
    });
    return { rule, label: `Annually on ${format(date, "LLLL dd")}` };
    // biome-ignore lint/style/noUselessElse: <it's intentional>
  } else if (fixed === "every-weekday") {
    rule = new RRule({
      freq: RRule.DAILY,
      interval: 1,
      byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
      dtstart: date,
      until: null
    });
    return { rule, label: "On every weekday (Monday to Friday)" };
    // biome-ignore lint/style/noUselessElse: <it's intentional>
  } else if (fixed === "custom") {
    rule = new RRule({
      freq: RRule.DAILY,
      interval: 1,
      byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
      dtstart: date,
      until: null // Will be set by the advanced form state logic
    });
  }
  return { rule };
}

export function ReserveFieldRecurring({
  defaultValue,
  form,
  errors,
  isCompact,
  office,
  resourceType,
  originalSchedule
}: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const isMobile = useIsMobile();

  const { isAdvancedRecurringOpenByDefault } = useReserveForm();

  const isRBFlag = isRBFeature(authenticatedUser);
  const name = "recurring";
  const [forceCustom, setForceCustom] = useState(false);
  const currentValue = form.watch(name, defaultValue);
  const [date, ...rest] = form.watch("weekDates");
  // Parse the RRULE string from form data to extract recurring options including the "until" date
  // currentValue comes from form.watch("recurring", defaultValue) where defaultValue is the existing reservation's RRULE
  const parsed = useMemo(() => {
    return parseRRuleString(currentValue, date);
  }, [currentValue, date]);

  // Determine if user is admin and bypass settings
  const isAdmin = canScheduleOthers(authenticatedUser);
  const bypassEnabled = useMemo(() => {
    if (!office || !resourceType) return false;

    if (resourceType === "workspace" || resourceType === "desk") {
      return authenticatedUser.offices?.byId[office.id]
        ?.workspace_reservation_period_recurring_bypass;
    }
    if (resourceType === "meeting_room") {
      return authenticatedUser.offices?.byId[office.id]
        ?.meeting_room_reservation_period_recurring_bypass;
    }
    return false;
  }, [office, resourceType]);

  const showNeverOption = isAdmin || bypassEnabled;

  // Check if the original schedule had a "never ending" recurring task
  const wasNeverEnding = useMemo(() => {
    return originalSchedule?.recurring_task?.end === "0001-01-01T00:00:00Z";
  }, [originalSchedule]);

  // Helper function to get the maximum date allowed for reservations
  const getMaxDateAllowed = useMemo(() => {
    if (!office || !date) return addDays(date ?? new Date(), 30);

    const extraDays = getReservationExtensionDays(
      authenticatedUser?.user?.email
    );
    const maxDays =
      (resourceType === "meeting_room"
        ? Math.round(office.meeting_room_reservation_period_min / 60 / 24)
        : Math.round(office.workspace_reservation_period_min / 60 / 24)) +
      extraDays;

    const result = addDays(date, maxDays);

    // Debug logging
    if (process.env.NODE_ENV === "development") {
      logError("getMaxDateAllowed debug:", {
        date: date.toISOString(),
        maxDays,
        result: result.toISOString(),
        resourceType,
        officePeriod:
          resourceType === "meeting_room"
            ? office.meeting_room_reservation_period_min
            : office.workspace_reservation_period_min
      });
    }

    return result;
  }, [office, date, resourceType]);

  // Initialize the advanced form state with the "ends on" value when modal opens
  // This sets the default until date based on existing recurring data or office settings
  const advancedFormState: AdvancedFormState = useMemo(() => {
    // Use origOptions when available to avoid invalid computed properties
    // Fall back to options if origOptions is not available
    const sourceOptions = parsed.origOptions || parsed.options;

    // Determine the default until date
    let defaultUntil: Date | null = sourceOptions.until ?? null;

    // If we don't have an existing until date and bypass is disabled, set to maxDays from today
    if (!defaultUntil && !showNeverOption && date) {
      defaultUntil = getMaxDateAllowed;
    }

    // Use origOptions.byweekday to preserve nth occurrence information (e.g., 4th Monday)
    // options.byweekday is null for monthly rules with BYDAY, so we must use origOptions
    const byweekday = sourceOptions.byweekday || [];

    return {
      interval: parsed.options.interval ?? 1,
      freq: parsed.options.freq,
      byweekday: Array.isArray(byweekday) ? (byweekday as Weekday[]) : [],
      until: defaultUntil
    };
  }, [parsed.options, showNeverOption, date, getMaxDateAllowed]);
  const [advanced, setAdvanced] = useState(
    isAdvancedRecurringOpenByDefault || false
  );

  const minUntilDate = date ? addDays(date, 1) : new Date();
  const isValidUntilDate =
    advancedFormState.until === null ||
    new Date(advancedFormState.until) >= minUntilDate;

  useEffect(() => {
    if (!isValidUntilDate) {
      setAdvancedFormState({
        ...advancedFormState,
        until: addDays(minUntilDate, 30)
      });
    }
  }, [isValidUntilDate]);

  const optionsTemplate = !isRBFlag
    ? recurringOptionsTemplate
    : recurringOptionsTemplateOnlyCustom;
  const customLabel = !isRBFlag ? " (custom)" : " (multiple)";

  const recurringOptions = useMemo(
    () =>
      date
        ? optionsTemplate.map((x) => {
            const defaultValue = getFixedDefaultValue(x.value, date);
            return {
              ...x,
              debugRRule: defaultValue.rule.toString(),
              label:
                date && x.value !== "no"
                  ? (defaultValue.label ??
                    formatLabel(
                      x.label,
                      date,
                      advancedFormState,
                      x.value === "custom" ? parsed : defaultValue.rule
                    ) + (x.value === "custom" ? customLabel : ""))
                  : x.label
            };
          })
        : [],
    [date, optionsTemplate, advancedFormState, currentValue]
  );

  let currentFixedValue =
    recurringOptions.find(
      (x) =>
        x.value === currentValue ||
        getFixedDefaultValue(x.value, date).rule.toString() === currentValue
    ) ?? recurringOptions[recurringOptions.length - 1];
  const isCustom =
    recurringOptions.indexOf(currentFixedValue) ===
      recurringOptions.length - 1 || forceCustom;
  if (isCustom) {
    currentFixedValue = recurringOptions[recurringOptions.length - 1];
  }

  type RecurringOptionData = {
    value: RecurringFixedValues;
    debugRRule?: string;
  };
  const items: UnifiedSelectItem<RecurringOptionData>[] = useMemo(
    () =>
      recurringOptions.map((x) => ({
        id: x.value,
        name: x.label,
        data: { value: x.value, debugRRule: x.debugRRule }
      })),
    [recurringOptions]
  );

  const selectedItem = currentFixedValue
    ? (items.find((item) => item.id === currentFixedValue.value) ?? null)
    : null;

  const setAdvancedFormState = (x: AdvancedFormState) => {
    const rrule = new RRule({
      freq: x.freq,
      interval: x.interval,
      byweekday: [...x.byweekday],
      dtstart: date,
      until: x.until
    });
    form.setValue(name, rrule.toString());
    setForceCustom(true);
  };

  if (!date || rest.length) {
    return null;
  }
  const maxDates = 10;
  const all = parsed.all((d, i) => i < maxDates + 1);

  return (
    <div className="mt-0 w-full">
      <>
        <FormField
          control={form.control}
          name={name}
          defaultValue={defaultValue}
          render={({ field }) => {
            const onChange = (v: (typeof recurringOptions)[0]) => {
              field.onChange(v);
              if (v) {
                if (v.value === "no") {
                  form.setValue(name, "no");
                  setForceCustom(false);
                } else {
                  if (v.value === "custom") {
                    setAdvanced(true);
                    setForceCustom(true);
                    form.setValue(name, getCleanRRuleString(parsed));
                  } else {
                    // For non-custom recurring options, set the appropriate until date
                    const fixedValue = getFixedDefaultValue(
                      v.value as RecurringFixedValues,
                      date
                    );

                    let untilDate = null;
                    if (!showNeverOption) {
                      // If bypass is disabled, auto-fill the furthest date that can be booked
                      untilDate = getMaxDateAllowed;
                    }

                    form.setValue(
                      name,
                      createCleanRRuleFromFixedValue(fixedValue.rule, untilDate)
                    );
                    setForceCustom(false);
                    setAdvanced(false);
                  }
                }
              }
            };

            if (isMobile) {
              return (
                <Select
                  value={currentFixedValue?.value || undefined}
                  onValueChange={(id) => {
                    onChange(recurringOptions.find((x) => x.value === id)!);
                  }}
                >
                  <SelectTrigger
                    showArrow={false}
                    className={`${inputV2Classes} flex align-center`}
                  >
                    <SelectValue className="w-full truncate" placeholder="-" />
                    <RecurringIcon className="h-4 w-4" />
                  </SelectTrigger>
                  <SelectContent className="z-dropdown">
                    {recurringOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value || ""}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }

            return (
              <UnifiedSelect
                items={items}
                value={selectedItem}
                onSelect={(item) => {
                  if (!item || Array.isArray(item)) return;
                  const option = recurringOptions.find(
                    (x) => x.value === item.id
                  );
                  if (option) onChange(option as never);
                }}
                triggerClassName="resource-select bg-[#f7f9fb] w-full max-w-full capitalize [.v2-reservation_&]:shadow-none min-h-[42px] rounded-lg border-none"
              />
            );
          }}
        />

        {errors.recurring && errors.recurring.type === "required" && (
          <p className="pl-1 text-primary">Required</p>
        )}
        {errors.recurring && errors.recurring.type !== "required" && (
          <p className="text-primary">
            Hmm, something went wrong. Can you try again?
          </p>
        )}

        <div className="flex items-center justify-end text-right text-sm">
          {currentValue !== "" && currentValue !== "no" && all.length ? (
            <Popover>
              <PopoverTrigger asChild>
                <span className="flex cursor-pointer items-center space-x-1">
                  <span className="">
                    (
                    {all.length > maxDates
                      ? `+${maxDates.toString()}`
                      : all.length}{" "}
                    dates)
                  </span>
                  <InformationCircleIcon className="mt-0.5 h-5 w-5 text-primary" />
                </span>
              </PopoverTrigger>
              <PopoverContent side="top" className="z-20 w-auto">
                <ul className="font-normal text-base text-neutral-800">
                  {all.map((d) => (
                    <li key={format(d, "yyyy-MM-dd")}>
                      {format(d, "MMM do (EEE), yyyy")}
                    </li>
                  ))}
                  {all.length > maxDates ? <li>...</li> : null}
                </ul>
              </PopoverContent>
            </Popover>
          ) : null}
        </div>

        {/* Always show "Ends on" field when recurring is selected */}
        {currentValue !== "" && currentValue !== "no" ? (
          <ReserveFieldRecurringAdvancedForm
            state={advancedFormState}
            setState={setAdvancedFormState}
            setAdvanced={setAdvanced}
            hideWeekends={isRBFlag}
            minUntilDate={minUntilDate}
            isCompact={isCompact}
            showNeverOption={showNeverOption}
            maxDateAllowed={getMaxDateAllowed}
            isCustom={isCustom}
            advanced={advanced}
            wasNeverEnding={wasNeverEnding}
          />
        ) : null}
      </>
    </div>
  );
}
